import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import CheckIcon from '../../assets/CheckIcon';
import CrossIcon from '../../assets/CrossIcon';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import useBgColorStore from '../../store/bgColor';

import { validateDateRange } from '../../utils/dateUtils';
import { IUpdateComplianceStaff } from '../../type/updateStaffCompliance';
import complianceApi from '../../api/complianceApi';
import { toast } from 'react-toastify';

interface ViewStaffComplianceFormProps {
  closeModal: () => void;
  complianceId?: number;
  staffId?: number;
  name?: string;
  compliance?: string;
  category?: string;
  recurrency?: string;
  status?: string;
  complianceLink?: string;
  nextComplianceDate?: string;
  lastComplianceDateDisplay?: string;
  note?: string;
  onSubmitSuccess?: () => void;
}

export default function ViewStaffComplianceOnBoarding({
  closeModal,
  complianceId,
  staffId,
  name,
  compliance,
  category,
  recurrency,
  status,
  complianceLink,
  nextComplianceDate,
  lastComplianceDateDisplay,
  note: initialNote,
  onSubmitSuccess,
}: ViewStaffComplianceFormProps) {
  const { bgColor } = useBgColorStore();

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IUpdateComplianceStaff>({
    defaultValues: {
      complianceId,
      staffId,
      lastComplianceDate: lastComplianceDateDisplay || new Date().toISOString(),
      nextComplianceDate: nextComplianceDate || new Date().toISOString(),
      note: initialNote || '',
    },
  });

  const lastDate = watch('lastComplianceDate');
  const nextDate = watch('nextComplianceDate');

  const onSubmit = async (data: IUpdateComplianceStaff) => {
    try {
      const response = await complianceApi.updateStaffComplianceDetails({
        ...data,
      });
      console.log(response, 'this is the value of the response');
      toast.success('updated successfully');
      closeModal();
      if (onSubmitSuccess) {
        onSubmitSuccess(); // ✅ Refresh parent
      }
    } catch (error) {
      toast.warn('details is not updated');
    }
  };

  // Handle calendar changes
  const handleFromDateChange = (date: Date | null) => {
    if (date) {
      const isoDate = date.toISOString();
      setValue('lastComplianceDate', isoDate);
      if (validateDateRange(date, new Date(nextDate))) {
        setValue('nextComplianceDate', isoDate);
      }
    }
  };

  const handleToDateChange = (date: Date | null) => {
    if (date) {
      setValue('nextComplianceDate', date.toISOString());
    }
  };

  return (
    <div className="max-w-xl mx-auto bg-sidebar-background p-4 rounded-lg shadow-md relative">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-text-blue">
          View Staff Compliance
        </h2>
        <button
          className="text-textColorGray hover:text-textColorGray absolute top-0 right-0 p-2"
          onClick={closeModal}
        >
          <RedCrossIcon />
        </button>
      </div>

      <div className="border-t-2 rounded-b-md border-light-blue" />
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="overflow-y-auto max-h-[calc(100vh-100px)] min-w-full grid grid-cols-2 gap-6">
          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Name</p>
            <p className="text-text-black">{name ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Compliance</p>
            <p className="text-text-black">{compliance ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Category</p>
            <p className="text-text-black">{category ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Recurrency</p>
            <p className="text-text-black">{recurrency ?? '-'}</p>
          </div>

          <div className="flex flex-col text-start">
            <p className="font-medium text-text-primary">Status</p>
            <p className="text-text-black">{status ?? '-'}</p>
          </div>
        </div>

        <div className="pt-4">
          <label className="block text-sm font-medium text-start text-text-primary">
            Note
          </label>
          <textarea
            rows={3}
            disabled
            placeholder="Write here..."
            className="mt-1 block w-full p-2 border border-customBorder-border rounded-md bg-backgroundColor-darkThemeColor"
            {...register('note')}
          />
        </div>

        {complianceLink && (
          <div className="pt-4 mt-2 w-full break-words">
            <label className="block text-sm font-medium text-text-primary text-start">
              Compliance Records
            </label>
            <div className="mt-1 text-start w-full">
              <a
                href={complianceLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline text-md block break-all"
              >
                {complianceLink}
              </a>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
